import clsx from "clsx";
import { FC, HTMLAttributes } from "react";
import TextSection, { ITextSectionContent } from "../common/TextSection";
import Image from "next/image";

type ITextWithImageProps = HTMLAttributes<HTMLDivElement> &
  ITextSectionContent & {
    image?: { src: string; alt: string };
    richTextStyle?: string;
    mediaRatio?: "sm" | "md" | "lg";
    mediaDirection?: "left" | "right";
    background_cover?: "full" | "contain";
    background?: "white" | "primary" | "secondary" | "primary_dark";
    mediaHeight?: number;
    mediaWidth?: number;
    innerContainerClass?: string;
  };

const bgColor = {
  primary: "bg-primary-100",
  secondary: "bg-secondary-50",
  white: "bg-white",
  primary_dark: "bg-primary-950",
};

const TextWithImage: FC<ITextWithImageProps> = ({
  image,
  heading,
  description,
  buttons,
  listItems,
  richTextStyle,
  mediaRatio = "sm",
  mediaDirection = "left",
  background_cover = "full",
  background = "white",
  mediaHeight = 415,
  mediaWidth = 362,
  className,
  innerContainerClass,
}) => {
  return (
    <section className={clsx("", className)}>
      {/* Single padding wrapper. Previously this had nested py-5/8/50 +
          py-10/16/100 which stacked to 60-150px of vertical empty space
          when background was full+tinted. Now one rule based on mode. */}
      <div
        className={clsx(
          background_cover === "full" && bgColor[background],
          background_cover === "full" && background !== "white"
            ? "py-10 lg:py-16 xl:py-[100px]"
            : "py-5 lg:py-8 xl:py-[50px]",
          "dark:bg-black-900 dark:py-0",
          innerContainerClass,
        )}
      >
          <div className="container">
            <div
              className={clsx(
                "grid grid-cols-1 items-center gap-y-7 lg:grid-cols-6 lg:gap-x-5",
                background_cover === "contain" && "p-6 dark:p-0 md:p-8 lg:p-12",
                (background_cover === "contain" &&
                  background === "primary" &&
                  "bg-primary-100 dark:bg-black-900") ||
                  (background === "secondary" &&
                    "bg-primary-50 dark:bg-black-900") ||
                  (background === "primary_dark" &&
                    "bg-primary-950 dark:bg-black-900") ||
                  (background === "white" && "bg-white dark:bg-black-900"),
              )}
            >
              {heading || description || buttons || listItems ? (
                <TextSection
                  heading={heading}
                  description={description}
                  buttons={buttons}
                  listItems={listItems}
                  textWithImageRichTextStyle={clsx(
                    richTextStyle,
                    background === "primary_dark" &&
                      "prose-headings:text-white prose-p:!text-white prose-strong:text-white prose-li:!text-white",
                  )}
                  headingClass={clsx(
                    background === "primary_dark" && "!text-white",
                  )}
                  className={clsx(
                    "!py-0",
                    mediaRatio === "sm" && "col-span-4",
                    mediaRatio === "md" && "col-span-3",
                    mediaRatio === "lg" && "col-span-2",

                    mediaRatio === "sm" && mediaDirection === "left"
                      ? background_cover === "full"
                        ? "lg:pl-12"
                        : "lg:pl-7"
                      : background_cover === "full" && "lg:pr-12",
                  )}
                  textSectionClass="!p-0"
                />
              ) : null}
              {image && (
                <Image
                  src={image.src}
                  alt={image.alt}
                  className={clsx(
                    "m-auto w-full self-start object-cover md:w-96 lg:mx-0",
                    mediaRatio === "sm" && "col-span-2",
                    mediaRatio === "md" && "col-span-3",
                    mediaRatio === "lg" && "col-span-4",
                    mediaDirection === "left" &&
                      "md:col-start-1 md:row-start-1",
                    mediaDirection === "right" && "justify-self-end",
                    "max-w-full",
                  )}
                  height={mediaHeight}
                  width={mediaWidth}
                />
              )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TextWithImage;
